// MSAL configuration for Azure AD authentication
// Falls back to Easy Auth if MSAL config is not provided

export const msalConfig = {
  auth: {
    // Client ID from Azure AD App Registration
    // Set via environment variable REACT_APP_AZURE_CLIENT_ID
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
    
    // Authority - use your Azure AD tenant ID
    // Format: https://login.microsoftonline.com/{tenant-id}
    // Or use 'common' for multi-tenant or 'organizations' for any org account
    authority: process.env.REACT_APP_AZURE_AUTHORITY || 'https://login.microsoftonline.com/common',
    
    // Redirect URI for authentication
    // Must match the redirect URI configured in Azure AD App Registration
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage', // Use localStorage for persistence across sessions
    storeAuthStateInCookie: false, // Set to true for IE11/Edge compatibility
  },
};

// Scopes for API access (used when calling protected APIs)
// Only set this if your backend API is a separate app registration
// For simple setups where frontend and backend use the same app registration,
// leave this empty and API calls will use the ID token instead
export const apiScopes = (() => {
  const scope = process.env.REACT_APP_AZURE_API_SCOPE;
  if (!scope) return [];
  
  const clientId = msalConfig.auth.clientId;
  // Check if API scope is self-referencing (same as client ID)
  // Azure AD doesn't allow apps to request tokens for themselves using api:// format
  const scopeClientId = scope.match(/^api:\/\/([^/]+)/)?.[1];
  if (scopeClientId === clientId) {
    console.warn('[msalConfig] API scope is self-referencing. Using ID token instead. Remove REACT_APP_AZURE_API_SCOPE or use a different API app registration.');
    return []; // Return empty to use ID token instead
  }
  
  return [scope];
})();

// Login request configuration
// For authentication, we only need basic OpenID Connect scopes
// These scopes provide user identity info in the ID token
// API access tokens can be requested separately when needed
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

// Request for API access token (separate from login)
// Use this when you need an access token to call your backend API
export const apiTokenRequest = (account) => {
  if (apiScopes.length > 0) {
    return {
      scopes: apiScopes,
      account: account,
    };
  }
  // If no API scope configured, return null (will use ID token instead)
  return null;
};

// Check if MSAL should be used (opt-in via environment variable)
// Default to Easy Auth unless explicitly enabled
export const shouldUseMsal = () => {
  return process.env.REACT_APP_USE_MSAL === 'true' || process.env.REACT_APP_USE_MSAL === '1';
};

// Shared MSAL instance (singleton pattern)
// This ensures both AuthContext and authAxios use the same instance
let sharedMsalInstance = null;

export const getSharedMsalInstance = async () => {
  if (!shouldUseMsal()) return null;
  
  if (sharedMsalInstance) {
    // Ensure it's initialized
    if (!sharedMsalInstance.getAllAccounts || typeof sharedMsalInstance.getAllAccounts !== 'function') {
      try {
        await sharedMsalInstance.initialize();
      } catch (err) {
        console.warn('[msalConfig] Failed to initialize shared MSAL instance:', err);
        return null;
      }
    }
    return sharedMsalInstance;
  }

  try {
    const { PublicClientApplication } = await import('@azure/msal-browser');
    sharedMsalInstance = new PublicClientApplication(msalConfig);
    await sharedMsalInstance.initialize();
    return sharedMsalInstance;
  } catch (err) {
    console.error('[msalConfig] Failed to create shared MSAL instance:', err);
    return null;
  }
};

export const setSharedMsalInstance = (instance) => {
  sharedMsalInstance = instance;
};

